import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  Edit,
  Delete,
  Lock,
  Add,
  Visibility,
  VisibilityOff,
  Security,
  TrendingUp,
  AccountBalance,
  Person,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import {
  fetchAllUsers,
  createUser,
  updateUser,
  changeUserPassword,
  deleteUser,
  User,
  TradeSettings,
  BrokerSettings,
} from "../services/userService";
import { AuthContext } from "../contexts/AuthContext";
import { toast } from "react-toastify";
import { formatDate } from "../utils/utils";

interface EditUserFormState {
  name: string;
  email: string;
  role: "admin" | "viewer";
  brokerSettings: {
    zerodha: {
      apiKey: string;
      apiSecret: string;
    };
  };
  tradeSettings: TradeSettings;
}

const defaultTradeSettings: TradeSettings = {
  ind: {
    capital: 1500000,
    baseRisk: 1.0,
    autoTradeEnabled: false,
    emergencyHalt: false,
  },
  us: {
    capital: 10000,
    baseRisk: 0.5,
    autoTradeEnabled: false,
    emergencyHalt: false,
  },
};

const UserManagement: React.FC = () => {
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Form states
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Add User Form
  const [addForm, setAddForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "viewer" as "admin" | "viewer",
  });

  // Edit User Form (profile, broker, trade configs)
  const [editForm, setEditForm] = useState<EditUserFormState>({
    name: "",
    email: "",
    role: "viewer",
    brokerSettings: {
      zerodha: {
        apiKey: "",
        apiSecret: "",
      },
    },
    tradeSettings: { ...defaultTradeSettings },
  });

  // Edit Form Tab Index
  const [editTab, setEditTab] = useState(0);
  const [showSecret, setShowSecret] = useState(false);

  // Password Reset Form
  const [passwordForm, setPasswordForm] = useState({
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllUsers();
      setUsers(data);
    } catch (err: any) {
      setError("Failed to fetch users. Please ensure you have administrator permissions.");
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  // Add User Submission
  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.name || !addForm.email || !addForm.password) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      await createUser(addForm);
      toast.success("User created successfully");
      setAddDialogOpen(false);
      setAddForm({ name: "", email: "", password: "", role: "viewer" });
      loadUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to create user");
    }
  };

  // Open Edit Dialog
  const handleOpenEdit = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role,
      brokerSettings: {
        zerodha: {
          apiKey: user.brokerSettings?.zerodha?.apiKey || "",
          apiSecret: "", // Do not retrieve actual secret from server
        },
      },
      tradeSettings: user.tradeSettings
        ? {
          ind: {
            capital: user.tradeSettings.ind?.capital ?? defaultTradeSettings.ind.capital,
            baseRisk: user.tradeSettings.ind?.baseRisk ?? defaultTradeSettings.ind.baseRisk,
            autoTradeEnabled: user.tradeSettings.ind?.autoTradeEnabled ?? defaultTradeSettings.ind.autoTradeEnabled,
            emergencyHalt: user.tradeSettings.ind?.emergencyHalt ?? false,
          },
          us: {
            capital: user.tradeSettings.us?.capital ?? defaultTradeSettings.us.capital,
            baseRisk: user.tradeSettings.us?.baseRisk ?? defaultTradeSettings.us.baseRisk,
            autoTradeEnabled: user.tradeSettings.us?.autoTradeEnabled ?? defaultTradeSettings.us.autoTradeEnabled,
            emergencyHalt: user.tradeSettings.us?.emergencyHalt ?? false,
          },
        }
        : { ...defaultTradeSettings },
    });
    setEditTab(0);
    setShowSecret(false);
    setEditDialogOpen(true);
  };

  // Edit User Submission
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      const payload: Partial<User> = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        tradeSettings: editForm.tradeSettings,
      };

      // Only update broker settings if apiKey is specified
      if (editForm.brokerSettings.zerodha.apiKey) {
        payload.brokerSettings = {
          zerodha: {
            apiKey: editForm.brokerSettings.zerodha.apiKey,
            apiSecret: editForm.brokerSettings.zerodha.apiSecret, // backend only updates if apiSecret is not empty
          },
        };
      }

      await updateUser(selectedUser.id, payload);
      toast.success("User settings updated successfully");
      setEditDialogOpen(false);
      loadUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to update user");
    }
  };

  // Open Password Dialog
  const handleOpenPassword = (user: User) => {
    setSelectedUser(user);
    setPasswordForm({ password: "", confirmPassword: "" });
    setPasswordDialogOpen(true);
  };

  // Password Submission
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.password !== passwordForm.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (passwordForm.password.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }
    if (!selectedUser) return;

    try {
      await changeUserPassword(selectedUser.id, { password: passwordForm.password });
      toast.success("Password updated successfully");
      setPasswordDialogOpen(false);
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to change password");
    }
  };

  // Open Delete Dialog
  const handleOpenDelete = (user: User) => {
    if (user.id === currentUser?.id) {
      toast.error("You cannot delete your own admin account");
      return;
    }
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  // Delete User Action
  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;
    try {
      await deleteUser(selectedUser.id);
      toast.success("User deleted successfully");
      setDeleteDialogOpen(false);
      loadUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to delete user");
    }
  };

  if (loading && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: 1200, mx: "auto" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: "text.primary" }}>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage users, role permissions, Zerodha API credentials, and live trading parameters in one place.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<Add />}
          onClick={() => setAddDialogOpen(true)}
          sx={{ borderRadius: 2, py: 1 }}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: "action.hover" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Zerodha Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Live Trade (IND)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Live Trade (US)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell sx={{ fontWeight: 500 }}>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={user.role.toUpperCase()}
                    color={user.role === "admin" ? "secondary" : "info"}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>
                  {user.isZerodhaLinked ? (
                    <Chip
                      icon={<CheckCircle sx={{ color: "success.main" }} fontSize="small" />}
                      label="Authenticated"
                      variant="outlined"
                      color="success"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  ) : user.hasBrokerSettings ? (
                    <Chip
                      icon={<Cancel sx={{ color: "warning.main" }} fontSize="small" />}
                      label="Not Authenticated"
                      variant="outlined"
                      color="error"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  ) : (
                    <Chip
                      label="Not Configured"
                      variant="outlined"
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {user.tradeSettings?.ind?.autoTradeEnabled ? (
                    <Box display="flex" flexDirection="column">
                      <Chip label="Live" color="success" size="small" variant="filled" sx={{ width: "fit-content" }} />
                      <Typography variant="caption" sx={{ fontFamily: "monospace", mt: 0.5 }}>
                        ₹{(user.tradeSettings.ind.capital).toLocaleString()}
                      </Typography>
                    </Box>
                  ) : (
                    <Chip label="Paper" size="small" variant="outlined" color="warning" />
                  )}
                </TableCell>
                <TableCell>
                  {user.tradeSettings?.us?.autoTradeEnabled ? (
                    <Box display="flex" flexDirection="column">
                      <Chip label="Live Enabled" color="success" size="small" variant="filled" sx={{ width: "fit-content" }} />
                      <Typography variant="caption" sx={{ fontFamily: "monospace", mt: 0.5 }}>
                        ${(user.tradeSettings.us.capital).toLocaleString()}
                      </Typography>
                    </Box>
                  ) : (
                    <Chip label="Disabled" size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  {formatDate(user.createdAt)}
                </TableCell>
                <TableCell align="right">
                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenEdit(user)}
                      title="Edit User Settings"
                      size="small"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="secondary"
                      onClick={() => handleOpenPassword(user)}
                      title="Change Password"
                      size="small"
                    >
                      <Lock fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleOpenDelete(user)}
                      disabled={user.id === currentUser?.id}
                      title={user.id === currentUser?.id ? "Cannot delete yourself" : "Delete User"}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <form onSubmit={handleAddSubmit}>
          <DialogTitle sx={{ fontWeight: 600 }}>Add New User</DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Name"
                required
                fullWidth
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
              />
              <TextField
                label="Email"
                type="email"
                required
                fullWidth
                value={addForm.email}
                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
              />
              <TextField
                label="Password"
                type="password"
                required
                fullWidth
                value={addForm.password}
                onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={addForm.role}
                  label="Role"
                  onChange={(e) => setAddForm({ ...addForm, role: e.target.value as any })}
                >
                  <MenuItem value="viewer">Viewer</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" color="secondary">
              Create
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <form onSubmit={handleEditSubmit}>
          <DialogTitle sx={{ fontWeight: 600 }}>Edit User: {selectedUser?.name}</DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <Tabs
              value={editTab}
              onChange={(_, value) => setEditTab(value)}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab icon={<Person fontSize="small" />} iconPosition="start" label="Profile" />
              <Tab icon={<Security fontSize="small" />} iconPosition="start" label="Broker Settings" />
              <Tab icon={<AccountBalance fontSize="small" />} iconPosition="start" label="Indian Trade Settings" />
              <Tab icon={<TrendingUp fontSize="small" />} iconPosition="start" label="US Trade Settings" />
            </Tabs>

            <Box p={3} minHeight={300}>
              {/* Tab 0: Profile Settings */}
              {editTab === 0 && (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Name"
                      required
                      fullWidth
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Email"
                      type="email"
                      required
                      fullWidth
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={editForm.role}
                        label="Role"
                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value as any })}
                      >
                        <MenuItem value="viewer">Viewer</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              {/* Tab 1: Broker Settings */}
              {editTab === 1 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Zerodha Kite Developer API Credentials
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    These parameters isolate execution environment for this user. Credentials will be stored with AES-256 encryption.
                  </Typography>

                  {selectedUser?.hasBrokerSettings && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Zerodha credentials are saved in the system. Enter new values to overwrite.
                    </Alert>
                  )}

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Kite API Key"
                        fullWidth
                        value={editForm.brokerSettings.zerodha.apiKey}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            brokerSettings: {
                              zerodha: {
                                ...editForm.brokerSettings.zerodha,
                                apiKey: e.target.value,
                              },
                            },
                          })
                        }
                        placeholder={selectedUser?.brokerSettings?.zerodha?.apiKey || "Enter Kite API Key"}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Kite API Secret"
                        type={showSecret ? "text" : "password"}
                        fullWidth
                        value={editForm.brokerSettings.zerodha.apiSecret}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            brokerSettings: {
                              zerodha: {
                                ...editForm.brokerSettings.zerodha,
                                apiSecret: e.target.value,
                              },
                            },
                          })
                        }
                        placeholder={selectedUser?.hasBrokerSettings ? "•••••••• (Saved)" : "Enter Kite API Secret"}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowSecret(!showSecret)} edge="end">
                                {showSecret ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Tab 2: Indian Trade Settings */}
              {editTab === 2 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Indian Market (NSE) AutoTrade Parameters
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editForm.tradeSettings.ind.autoTradeEnabled}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              tradeSettings: {
                                ...editForm.tradeSettings,
                                ind: {
                                  ...editForm.tradeSettings.ind,
                                  autoTradeEnabled: e.target.checked,
                                },
                              },
                            })
                          }
                          color="success"
                        />
                      }
                      label="Enable Auto Trading (NSE)"
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Trading Capital (INR)"
                        type="number"
                        fullWidth
                        value={editForm.tradeSettings.ind.capital}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            tradeSettings: {
                              ...editForm.tradeSettings,
                              ind: {
                                ...editForm.tradeSettings.ind,
                                capital: Number(e.target.value),
                              },
                            },
                          })
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Base Risk Per Trade (%)"
                        type="number"
                        inputProps={{ step: 0.1, min: 0.1, max: 10 }}
                        fullWidth
                        value={editForm.tradeSettings.ind.baseRisk}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            tradeSettings: {
                              ...editForm.tradeSettings,
                              ind: {
                                ...editForm.tradeSettings.ind,
                                baseRisk: Number(e.target.value),
                              },
                            },
                          })
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Tab 3: US Trade Settings */}
              {editTab === 3 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    US Market (NYSE/NASDAQ) AutoTrade Parameters
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editForm.tradeSettings.us.autoTradeEnabled}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              tradeSettings: {
                                ...editForm.tradeSettings,
                                us: {
                                  ...editForm.tradeSettings.us,
                                  autoTradeEnabled: e.target.checked,
                                },
                              },
                            })
                          }
                          color="success"
                        />
                      }
                      label="Enable Auto Trading (US)"
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Trading Capital (USD)"
                        type="number"
                        fullWidth
                        value={editForm.tradeSettings.us.capital}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            tradeSettings: {
                              ...editForm.tradeSettings,
                              us: {
                                ...editForm.tradeSettings.us,
                                capital: Number(e.target.value),
                              },
                            },
                          })
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Base Risk Per Trade (%)"
                        type="number"
                        inputProps={{ step: 0.1, min: 0.1, max: 10 }}
                        fullWidth
                        value={editForm.tradeSettings.us.baseRisk}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            tradeSettings: {
                              ...editForm.tradeSettings,
                              us: {
                                ...editForm.tradeSettings.us,
                                baseRisk: Number(e.target.value),
                              },
                            },
                          })
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" color="secondary">
              Save Changes
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="xs" fullWidth>
        <form onSubmit={handlePasswordSubmit}>
          <DialogTitle sx={{ fontWeight: 600 }}>Reset Password for: {selectedUser?.name}</DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="New Password"
                type="password"
                required
                fullWidth
                value={passwordForm.password}
                onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
              />
              <TextField
                label="Confirm New Password"
                type="password"
                required
                fullWidth
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" color="secondary">
              Reset Password
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm User Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete the user <strong>{selectedUser?.name}</strong> (
            {selectedUser?.email})?
          </Typography>
          <Typography color="error" variant="body2" sx={{ mt: 2, fontWeight: 500 }}>
            Warning: This will clear all access credentials and records associated with this user account from the server database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
